/**
 * Ensures a blob has an image MIME type. Android WebView fetch() often returns
 * empty or application/octet-stream for Capacitor camera webPaths.
 */
export function ensureImageBlob(blob: Blob, fallbackType = 'image/jpeg'): Blob {
  if (blob?.type?.startsWith('image/')) {
    return blob;
  }
  return new Blob([blob], { type: fallbackType });
}

/**
 * Bakes EXIF orientation into pixel data so the cropper receives a consistently oriented image.
 * Falls back to the original blob when normalization is unavailable.
 * Downscales very large images to reduce Android WebView canvas / toBlob failures.
 */
export async function normalizeImageOrientation(
  blob: Blob,
  maxDimension = 2000
): Promise<Blob> {
  const typedBlob = ensureImageBlob(blob);
  if (!typedBlob.type.startsWith('image/')) {
    return typedBlob;
  }

  try {
    const bitmap = await createImageBitmap(typedBlob, { imageOrientation: 'from-image' });
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return typedBlob;
    }

    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const mimeType = typedBlob.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const normalized = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, mimeType, 0.92);
    });

    return normalized || typedBlob;
  } catch {
    return typedBlob;
  }
}
